import { ApiDateFilter } from "@/components/date-filter/date-filter.type.ts";
import { ApiCustomField, ApiTableTypes } from "@/types/custom-field.types.ts";

// Определяет рендер ячейки
export enum ApiEmployeeColumnValueType {
  text = "text",
  number = "number",
  money = "money",
  date = "date",
  datetime = "datetime",
  bool = "bool",
  select = "select",
  employee = "employee",
}

// Определяет виджет фильтра; none — колонка не фильтруется и не сортируется
export enum ApiEmployeeColumnFilterType {
  text = "text",
  number = "number",
  date = "date",
  select = "select",
  bool = "bool",
  employee = "employee",
  none = "none",
}

// id опции: число у справочников и опций кастомных полей, строка у enum
// (lifecycle_status, gender, compensation.per_type, additional_compensation.*).
// В option_ids уезжает как пришёл, без приведения типа.
export type ApiEmployeeColumnOptionId = number | string;

export interface ApiEmployeeColumnOption {
  id: ApiEmployeeColumnOptionId;
  title: string;
}

export interface ApiEmployeeColumn {
  id: string;
  title: string;
  value_type: ApiEmployeeColumnValueType;
  filter_type: ApiEmployeeColumnFilterType;
  is_sortable: boolean;
  is_filterable: boolean;
  // У сотрудника несколько значений (строки таблицы, доп. компенсации) — ячейка показывает список
  is_multi_row: boolean;
  options?: ApiEmployeeColumnOption[] | null;
  field_id: number | null;
  table_id: number | null;
}

// Группа колонки в UI — второй сегмент ключа; всё остальное это личные данные
export enum EmployeeColumnEntity {
  personal = "personal",
  position = "position",
  employment = "employment",
  compensation = "compensation",
  termination = "termination",
  additional_compensation = "additional_compensation",
  custom_field = "custom_field",
  custom_table = "custom_table",
}

const KEY_PREFIX = "employee.";
const CURRENCY_SUFFIX = ".currency";

function splitCurrencySuffix(slug: string): { slug: string; isCurrency: boolean } {
  return slug.endsWith(CURRENCY_SUFFIX)
    ? { slug: slug.slice(0, -CURRENCY_SUFFIX.length), isCurrency: true }
    : { slug, isCurrency: false };
}

/**
 * Сущность колонки по её ключу. Ключи не хардкодятся: каталог приходит с бэка и
 * зависит от прав, поэтому группа вычисляется из самого ключа.
 */
export function getEmployeeColumnEntity(columnId: string): EmployeeColumnEntity {
  if (!columnId.startsWith(KEY_PREFIX)) return EmployeeColumnEntity.personal;
  const segment = columnId.slice(KEY_PREFIX.length).split(".")[0];
  return (
    Object.values(EmployeeColumnEntity).find((entity) => entity === segment) ??
    EmployeeColumnEntity.personal
  );
}

export function isEmployeeColumnKey(columnId: string): boolean {
  return columnId.startsWith(KEY_PREFIX);
}

/** Slug кастомного поля из ключа `employee.custom_field.<slug>`. */
export function parseCustomFieldKey(
  columnId: string
): { slug: string; isCurrency: boolean } | undefined {
  const entity = getEmployeeColumnEntity(columnId);
  if (entity !== EmployeeColumnEntity.custom_field) return undefined;
  const parsed = splitCurrencySuffix(columnId.slice(`${KEY_PREFIX}custom_field.`.length));
  return parsed.slug ? parsed : undefined;
}

// Сущность ключа → тип её системной таблицы
export const SYSTEM_TABLE_TYPE_BY_ENTITY: Partial<Record<EmployeeColumnEntity, ApiTableTypes>> = {
  [EmployeeColumnEntity.position]: ApiTableTypes.employee_positions,
  [EmployeeColumnEntity.employment]: ApiTableTypes.employee_employments,
  [EmployeeColumnEntity.compensation]: ApiTableTypes.employee_compensations,
  [EmployeeColumnEntity.additional_compensation]: ApiTableTypes.employee_additional_compensations,
  [EmployeeColumnEntity.termination]: ApiTableTypes.employee_terminations,
};

/**
 * Своя колонка системной таблицы: `employee.<сущность>.<slug>`. Slug колонки —
 * `<slug таблицы>-<…>`, а slug системной таблицы — `table_<table_type>`
 * (`cf_registry_sync_service.py`), поэтому с хвостом системной колонки он не
 * совпадает. `table_id` в ключе нет: значения сопоставляются по `table_type`.
 */
export function parseSystemTableCustomFieldKey(
  columnId: string
): { slug: string; tableType: ApiTableTypes; isCurrency: boolean } | undefined {
  const entity = getEmployeeColumnEntity(columnId);
  const tableType = SYSTEM_TABLE_TYPE_BY_ENTITY[entity];
  if (!tableType) return undefined;

  const { slug, isCurrency } = splitCurrencySuffix(
    columnId.slice(`${KEY_PREFIX}${entity}.`.length)
  );
  if (slug.includes(".") || !slug.startsWith(`table_${tableType}-`)) return undefined;
  return { slug, tableType, isCurrency };
}

/** `table_id` и slug столбца из ключа `employee.custom_table.<table_id>.<slug>`. */
export function parseCustomTableKey(
  columnId: string
): { tableId: number; slug: string; isCurrency: boolean } | undefined {
  const entity = getEmployeeColumnEntity(columnId);
  if (entity !== EmployeeColumnEntity.custom_table) return undefined;
  const tail = columnId.slice(`${KEY_PREFIX}custom_table.`.length);
  const separatorIndex = tail.indexOf(".");
  if (separatorIndex <= 0) return undefined;
  const tableId = Number(tail.slice(0, separatorIndex));
  const { slug, isCurrency } = splitCurrencySuffix(tail.slice(separatorIndex + 1));
  if (!Number.isFinite(tableId) || !slug) return undefined;
  return { tableId, slug, isCurrency };
}

export function buildCustomTableKey(tableId: number, slug: string): string {
  return `${KEY_PREFIX}custom_table.${tableId}.${slug}`;
}

/** Поле доп. компенсации из ключа `employee.additional_compensation.<поле>`. */
export function getAdditionalCompensationField(columnId: string): string | undefined {
  const entity = getEmployeeColumnEntity(columnId);
  if (entity !== EmployeeColumnEntity.additional_compensation) return undefined;
  return columnId.slice(`${KEY_PREFIX}additional_compensation.`.length) || undefined;
}

// Строка таблицы в ответе поиска. У исторической таблицы приходит только
// актуальная строка; у системной — текущая запись сущности (`id` — id записи),
// у разовых выплат — выплаты текущего месяца.
export interface ApiEmployeeSearchCustomTableRow {
  id: number;
  date_effective_from: Date | null;
  is_current: boolean;
  cells: ApiCustomField[];
}

export interface ApiEmployeeSearchCustomTable {
  table_id: number;
  table_type: ApiTableTypes;
  rows: ApiEmployeeSearchCustomTableRow[];
}

// ============================================================================
// ФИЛЬТРЫ ПО КОЛОНКАМ
// ============================================================================

/**
 * Элемент любого из семи списков. Какие поля значения заполнять — определяет
 * `filter_type` колонки; `is_empty` доступен для колонки с любым типом.
 */
export interface ApiEmployeeColumnFilter {
  field: string;
  text?: string;
  text_array?: string[];
  number_from?: number;
  number_to?: number;
  date_filter?: ApiDateFilter;
  datetime_from?: Date;
  datetime_to?: Date;
  option_ids?: ApiEmployeeColumnOptionId[];
  switch?: boolean;
  employee_ids?: number[];
  is_empty?: boolean;
}

export interface EmployeeColumnFilterCategory {
  id: string;
  title: string;
  columns: EmployeeColumnFilterColumn[];
}

export interface EmployeeColumnFilterColumn extends ApiEmployeeColumn {
  groupId?: string;
  groupTitle?: string;
}

// Списки фильтров в теле поиска: по одному на сущность, соединяются через AND
export interface ApiEmployeeColumnFilters {
  personal_filter?: ApiEmployeeColumnFilter[];
  position_filter?: ApiEmployeeColumnFilter[];
  employment_filter?: ApiEmployeeColumnFilter[];
  compensation_filter?: ApiEmployeeColumnFilter[];
  termination_filter?: ApiEmployeeColumnFilter[];
  additional_compensation_filter?: ApiEmployeeColumnFilter[];
  custom_fields_filter?: ApiEmployeeColumnFilter[];
  custom_table_filter?: ApiEmployeeColumnFilter[];
}

export type EmployeeColumnFilterKey = keyof ApiEmployeeColumnFilters;

const FILTER_KEY_BY_ENTITY: Record<EmployeeColumnEntity, EmployeeColumnFilterKey> = {
  [EmployeeColumnEntity.personal]: "personal_filter",
  [EmployeeColumnEntity.position]: "position_filter",
  [EmployeeColumnEntity.employment]: "employment_filter",
  [EmployeeColumnEntity.compensation]: "compensation_filter",
  [EmployeeColumnEntity.termination]: "termination_filter",
  [EmployeeColumnEntity.additional_compensation]: "additional_compensation_filter",
  [EmployeeColumnEntity.custom_field]: "custom_fields_filter",
  [EmployeeColumnEntity.custom_table]: "custom_table_filter",
};

/**
 * Список, в который уезжает фильтр по этой колонке. Ключ в чужом списке — 422
 * `employee_column_filter_invalid`, поэтому раскладка идёт строго по префиксу.
 */
export function getFilterKeyForColumn(columnId: string): EmployeeColumnFilterKey {
  if (parseSystemTableCustomFieldKey(columnId)) return "custom_table_filter";
  return FILTER_KEY_BY_ENTITY[getEmployeeColumnEntity(columnId)];
}

/** Раскладывает плоский список фильтров по семи спискам тела запроса. */
export function groupColumnFilters(filters: ApiEmployeeColumnFilter[]): ApiEmployeeColumnFilters {
  const grouped: ApiEmployeeColumnFilters = {};
  for (const filter of filters) {
    const key = getFilterKeyForColumn(filter.field);
    (grouped[key] ??= []).push(filter);
  }
  return grouped;
}
